import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AuthenticationService } from 'app/core/authentication';
import { emailRegExp } from 'app/core/form-validators/email.regexp';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { LanguagesService } from 'app/core/languages/languages.service';
import { LocaleModel } from 'app/core/languages/locale-model';
import { LocalizationService } from 'app/core/localization/localization.service';
import { ModalService } from 'app/core/modals/services/modal.service';
import { DefaultPageRouteService } from 'app/core/navigation';
import { LocalizedToasterService } from 'app/core/toaster/localized-toaster.service';
import { PARENT_ROUTE } from 'app/parent/parent-routing.constant';
import { InputTypes } from 'app/shared/enums/input-types.enum';
import { UpdatePasswordModel } from 'app/shared/models/update-password-model';
import { UserInfoParentSettings } from 'app/shared/models/user-info-parent-settings.model';
import { UserInfoSettings, UserRoleInfo } from 'app/shared/models/user-info-settings.model';
import { ValidationMessages } from 'app/shared/models/validation-messages.model';
import { AccountSettingsDataService } from 'app/shared/services/account-settings-data-service';
import { SettingsValueService } from 'app/shared/services/settings-value-service';
import { TEACHERS_ROUTE } from 'app/teachers/teachers-routing.constant';
import { CancelSubscriptionModalComponent } from '../../cancel-subscription-modal/cancel-subscription-modal.component';
import { TeacherDeleteAccountModalComponent } from '../../teacher-delete-account-modal/teacher-delete-account-modal.component';

import { EmailNotificationsModalComponent } from './../../email-notifications-modal/email-notifications-modal.component';

@UntilDestroy()
@Component({
  selector: 'kh-settings-view',
  styleUrls: ['./settings-view.component.less'],
  templateUrl: './settings-view.component.html',
  standalone: false
})
export class SettingsViewComponent implements OnInit {
  public settingsInfo$?: Observable<UserInfoSettings>;
  public settingsInfo?: UserInfoSettings;
  public inputTypes = InputTypes;
  public disableSaveButton = false;
  public externalIds: string[] = [];
  public isRosteredUser = false;
  public selectedRole = '';
  public hideTeacherSection = false;

  public listOfControls = {
    deleteAccount: 'deleteAccount',
    displayName: 'displayName',
    email: 'email',
    emailNotifications: 'emailNotifications',
    language: 'language',
    password: 'password'
  };

  public displayNameForm!: FormGroup;
  public displayNameFormResetValue = {};
  public displayNameFormFields = {
    firstName: 'firstName',
    lastName: 'lastName'
  };

  public passwordForm!: FormGroup;
  public passwordSuccessMessage = '';

  public passwordFormFields = {
    currentPassword: 'currentPassword',
    newPassword: 'newPassword',
    newPasswordConfirm: 'newPasswordConfirm'
  };

  public passwordValidationMessages: ValidationMessages = {
    custom: '',
    incorrectPassword: '',
    minlength: '',
    mismatch: '',
    required: ''
  };

  public locales$: Observable<LocaleModel[]> = EMPTY;
  public languageForm!: FormGroup;
  public languageFormFields = {
    locale: 'locale'
  };

  public emailForm!: FormGroup;
  public isEmailVerified!: boolean;
  public verificationEmailSent = false;
  public emailFormResetValue = {};
  public emailFormFields = {
    email: 'email'
  };

  public emailValidationMessages: ValidationMessages = {
    emailRequired: '',
    emailInvalid: ''
  };

  public activeText = '';

  private readonly formBuilder = inject(FormBuilder);
  private readonly accountSettingsDataService = inject(AccountSettingsDataService);
  private readonly toaster = inject(LocalizedToasterService);
  private readonly settingsValueService = inject(SettingsValueService);
  private readonly languagesService = inject(LanguagesService);
  private readonly modalService = inject(ModalService);
  private readonly _translationService = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly defaultPageRouteService = inject(DefaultPageRouteService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly localizationService = inject(LocalizationService);

  public ngOnInit(): void {
    this.locales$ = this.languagesService.getAvailableLocales();
    this.displayNameForm = this.createDisplayNameForm();
    this.passwordForm = this.createPasswordForm();
    this.languageForm = this.createLanguageForm();
    this.emailForm = this.createEmailForm();

    this._translationService.translationsReady$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.activeText = this._translationService.instant('SETTINGS_VIEW.ACTIVE');
        this.passwordSuccessMessage = this._translationService.instant('SETTINGS_VIEW.PASSWORD_UPDATED_SUCCESSFULLY');

        this.passwordValidationMessages = {
          custom: '',
          incorrectPassword: this._translationService.instant('SETTINGS_VIEW.CURRENT_PASSWORD_IS_INCORRECT_PLEASE'),
          minlength: this._translationService.instant('SETTINGS_VIEW.PASSWORD_MUST_BE_4_CHARACTERS'),
          mismatch: this._translationService.instant('SETTINGS_VIEW.NEW_PASSWORDS_DONT_MATCH_PLEASE'),
          required: this._translationService.instant('SETTINGS_VIEW.THIS_FIELD_IS_REQUIRED')
        };

        this.emailValidationMessages = {
          emailRequired: this._translationService.instant('SETTINGS_VIEW.EMAIL_IS_REQUIRED'),
          emailInvalid: this._translationService.instant('SETTINGS_VIEW.EMAIL_NOT_VALID')
        };
      });

    this.settingsInfo$ = this.accountSettingsDataService.getSettingsInfo().pipe(
      tap((res) => {
        this.settingsInfo = res;
        this.externalIds = res.ExternalIds;
        if (res.TeacherInfo && res.UserRoles.length > 1) {
          this.hideTeacherSection = true;
        }

        this.displayNameFormResetValue = {
          [this.displayNameFormFields.firstName]: res.FirstName ?? '',
          [this.displayNameFormFields.lastName]: res.LastName ?? ''
        };

        this.displayNameForm?.setValue(this.displayNameFormResetValue);
        this.languageForm?.setValue(
          { [this.languageFormFields.locale]: res.Locale ?? this.localizationService.defaultLocaleId },
          { emitEvent: false }
        );
        this.subscribeToLanguageChanges();

        this.emailFormResetValue = {
          [this.emailFormFields.email]: res.Email ?? ''
        };

        this.emailForm?.setValue(this.emailFormResetValue);
        this.isEmailVerified = res.EmailConfirmed;
        this.isRosteredUser = this.externalIds.some((str) => str.toLowerCase().includes('edlink'));

        this.changeDetector.detectChanges();
      })
    );
  }

  public updateActiveForm(newActive: string, disableSaveButton = false): void {
    this.settingsValueService.activeInstance = newActive;
    this.settingsValueService.disableSaveButtonValue = disableSaveButton;
  }

  public displayDeleteAccountModal(): void {
    this.settingsValueService.activeInstance = this.listOfControls.deleteAccount;
    this.modalService.displayCustomModal<TeacherDeleteAccountModalComponent>(
      TeacherDeleteAccountModalComponent,
      {
        className: 'modal-centered user-settings-modal delete-user'
      }
    );
  }

  private createDisplayNameForm(): FormGroup {
    const nameValidators = [Validators.required, Validators.pattern(/.*\S.*/)];
    return this.formBuilder.group({
      [this.displayNameFormFields.firstName]: [undefined, nameValidators],
      [this.displayNameFormFields.lastName]: [undefined, nameValidators]
    });
  }

  public saveDisplayName(): void {
    if (!this.displayNameForm?.valid) {
      return;
    }
    const request = this.displayNameForm?.value;
    this.accountSettingsDataService
      .saveName({ FirstName: request.firstName?.trim(), LastName: request.lastName?.trim() })
      .pipe(
        tap((res) => {
          this.displayNameFormResetValue = {
            [this.displayNameFormFields.firstName]: res.FirstName,
            [this.displayNameFormFields.lastName]: res.LastName
          };

          this.toaster.success('SETTINGS_VIEW.DISPLAY_NAME_UPDATED');
          this.settingsValueService.toggleEditing.emit(
            this.listOfControls.displayName
          );
        })
      )
      .subscribe();
  }

  public resetDisplayName(): void {
    this.displayNameForm?.reset(this.displayNameFormResetValue);
  }

  private createPasswordForm(): FormGroup {
    const formGroup = this.formBuilder.group(
      {
        [this.passwordFormFields.currentPassword]: [null, Validators.required],
        [this.passwordFormFields.newPassword]: [null, [Validators.required, Validators.minLength(4)]],
        [this.passwordFormFields.newPasswordConfirm]: [null]
      },
      {
        updateOn: 'change',
        validators: this.getPasswordValidator
      }
    );

    formGroup.valueChanges.subscribe(() => {
      this.allowSave();
    });

    return formGroup;
  }

  private getPasswordValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (control !== null) {
      const newPassword = control.get(this.passwordFormFields.newPassword);
      const confirmationPassword = control.get(
        this.passwordFormFields.newPasswordConfirm
      );

      if (newPassword?.dirty && confirmationPassword?.dirty) {
        const newPasswordValue =
          control.value[this.passwordFormFields.newPassword];
        const confirmationPasswordValue =
          control.value[this.passwordFormFields.newPasswordConfirm];

        if (newPasswordValue !== confirmationPasswordValue) {
          confirmationPassword.setErrors({ mismatch: true });
        }
        else {
          confirmationPassword?.setErrors(null);
        }
      }
    }

    return null;
  };

  public allowSave() {
    const currentPassword = this.passwordForm.get(
      this.passwordFormFields.currentPassword
    );
    const newPassword = this.passwordForm.get(
      this.passwordFormFields.newPassword
    );
    const confirmationPassword = this.passwordForm.get(
      this.passwordFormFields.newPasswordConfirm
    );

    const currentPasswordDirty = !!currentPassword?.dirty;
    const newPasswordDirty = !!newPassword?.dirty;
    const confirmationPasswordDirty = !!confirmationPassword?.dirty;

    this.settingsValueService.disableSaveButtonValue =
      !this.passwordForm?.valid
      || !currentPasswordDirty
      || !newPasswordDirty
      || !confirmationPasswordDirty;
  }

  public updatePassword(): void {
    if (this.passwordForm.valid) {
      const passwordRequest: UpdatePasswordModel = {
        currentPassword:
          this.passwordForm.value[this.passwordFormFields.currentPassword],
        newPassword:
          this.passwordForm.value[this.passwordFormFields.newPassword]
      };

      this.accountSettingsDataService
        .updatePassword(passwordRequest)
        .pipe(
          tap(() => {
            this.toaster.success(this.passwordSuccessMessage);
            this.resetPasswordForm();
            this.settingsValueService.toggleEditing.emit(
              this.listOfControls.password
            );
          }),
          catchError((error) => {
            if (error.status === 400 && error.error.Errors?.length) {
              const currentPassword = this.passwordForm.get(
                this.passwordFormFields.currentPassword
              );
              if (currentPassword) {
                currentPassword?.setErrors({ incorrectPassword: true });
              }
            }

            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  public resetPasswordForm(): void {
    this.passwordForm.reset();
  }

  private createLanguageForm(): FormGroup {
    return this.formBuilder.group({
      [this.languageFormFields.locale]: [undefined, Validators.required]
    });
  }

  private subscribeToLanguageChanges(): void {
    this.languageForm
      .get(this.languageFormFields.locale)
      ?.valueChanges.subscribe((changes) => {
        this.setLanguage(changes);
      });
  }

  public setLanguage(language: string): void {
    this.accountSettingsDataService.setLocale(language).subscribe(() => {
      this.updateActiveForm('');
      const userClaims = this.authenticationService.getUserClaims();
      if (!userClaims) {
        return;
      }

      userClaims.localeId = language;
      this.authenticationService.setUserClaims(userClaims);
    });
  }

  public getCurrentLanguage(locales: LocaleModel[] | null): string {
    if (!locales) {
      return '';
    }

    const value =
      this.languageForm.controls[this.languageFormFields.locale].value;

    const found = locales.filter((x) => x.LocaleId === value);

    if (found) {
      return found[0].Language;
    }

    return '';
  }

  public getLocaleName = (language: LocaleModel) => language?.Language;
  public getLocaleValue = (language: LocaleModel) => language?.LocaleId;

  public getLanguagePlaceholder(locales: LocaleModel[]): string | undefined {
    const chosen =
      this.languageForm.controls[this.languageFormFields.locale].value;
    const foundObject = locales.find((loc) => loc.LocaleId === chosen);

    return foundObject?.Language;
  }

  private emailRequiredValidator = (control: AbstractControl): ValidationErrors | null =>
    !control.value || !(control.value as string).trim() ? { emailRequired: true } : null;

  private emailFormatValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value || !value.trim()) {
      return null; // let emailRequiredValidator handle empty
    }
    return emailRegExp.test(value.trim()) ? null : { emailInvalid: true };
  };

  private createEmailForm(): FormGroup {
    const form = this.formBuilder.group({
      [this.emailFormFields.email]: [undefined, [this.emailRequiredValidator, this.emailFormatValidator]]
    });

    form.valueChanges.subscribe(() => {
      if (this.settingsValueService.activeInstance === this.listOfControls.email) {
        this.settingsValueService.disableSaveButtonValue = !form.valid;
      }
    });

    return form;
  }

  public saveEmail(): void {
    if (!this.emailForm?.valid) {
      this.emailForm.markAllAsTouched();
      return;
    }
    const request = this.emailForm?.value;

    this.accountSettingsDataService
      .saveEmail(request.email)
      .pipe(
        tap((res) => {
          if (!res.IsValid) {
            this.toaster.error('SETTINGS_VIEW.EMAIL_ACCOUNT_ALREADY_EXISTS');
            this.resetEmail();

            return;
          }

          this.emailFormResetValue = {
            [this.emailFormFields.email]: request.email
          };

          this.toaster.success('SETTINGS_VIEW.EMAIL_UPDATED');
          this.settingsValueService.toggleEditing.emit(
            this.listOfControls.email
          );
        })
      )
      .subscribe();
  }

  public resetEmail(): void {
    this.emailForm?.reset(this.emailFormResetValue);
  }

  public sendEmailVerification(): void {
    this.accountSettingsDataService
      .resendEmailVerification()
      .pipe(
        tap(() => {
          this.verificationEmailSent = true;
          this.toaster.success('SETTINGS_VIEW.EMAIL_VERIFICATION_SENT');
        })
      )
      .subscribe();
  }

  private get emailGroups() {
    return this.settingsInfo?.TeacherInfo?.EmailGroups || this.settingsInfo?.ParentInfo?.EmailGroups;
  }

  public showEmailNotificationsModal(): void {
    this.settingsValueService.activeInstance =
      this.listOfControls.deleteAccount;
    const modalRef = this.modalService.displayCustomModal<EmailNotificationsModalComponent>(
      EmailNotificationsModalComponent,
      {
        className: 'modal-centered user-settings-modal',
        data: {
          emailGroups: this.emailGroups
        }
      }
    );

    modalRef.content?.saveCallback.subscribe((userGroups) => {
      userGroups.forEach((userGroup) => {
        const emailGroup = this.emailGroups
          ?.find((x) => x.EmailGroupId === userGroup.EmailGroupId);

        if (emailGroup?.UserGroup) {
          emailGroup.UserGroup = userGroup;
        }
      });
    });
  }

  public async goToMySchool(): Promise<void> {
    await this.router.navigate(TEACHERS_ROUTE.SCHOOL.route());
  }

  public async goToSubscribe(): Promise<void> {
    await this.router.navigate(TEACHERS_ROUTE.SUBSCRIBE.route());
  }

  public async goToRole(role: string): Promise<void> {
    await this.defaultPageRouteService.routeToDefaultSelectedRole(role);
  }

  public async goToParentalControls(): Promise<void> {
    await this.router.navigate(
      PARENT_ROUTE.ADVANCED_PARENTAL_CONTROLS.noStudentRoute()
    );
  }

  public async goToMergeAccount(): Promise<void> {
    await this.router.navigate(
      TEACHERS_ROUTE.MERGE_ACCOUNT.route()
    );
  }

  public getLinkedAccounts(): string {
    return this.externalIds.join(', ');
  }

  public showCancelSubscriptionModal(): void {
    this.modalService.displayCustomModal<CancelSubscriptionModalComponent>(
      CancelSubscriptionModalComponent,
      {
        className: 'settings-modal modal-sm modal-centered'
      }
    );
  }

  public hasActivePremiumSubscriptions(): boolean {
    const numPurchased = this.settingsInfo?.TeacherInfo?.PremiumSubscriptions?.NumPurchased ?? 0;
    const cancelledDate = this.settingsInfo?.TeacherInfo?.PremiumSubscriptions?.CancelledDate ?? null;

    return numPurchased > 0 && cancelledDate === null;
  }

  public classSubscriptionsLabel(): string {
    return `${this.settingsInfo?.TeacherInfo?.PremiumSubscriptions?.NumPurchased} ${this.activeText}`;
  }

  public getRoleValue(role: UserRoleInfo): string {
    return role.Name;
  }

  public getRoleLabel(role: UserRoleInfo): string {
    return role.LocalizedName ?? role.Name;
  }

  public hasChildWithPremium(parentInfo: UserInfoParentSettings): boolean {
    return parentInfo?.Students?.some((s) => s.IsSubscribed === true) ?? false;
  }

  public getChildrenLabel(parentInfo: UserInfoParentSettings): string {
    const count = parentInfo?.Students?.length ?? 0;
    const childText = count === 1
      ? this._translationService.instant('SETTINGS_VIEW.CHILD')
      : this._translationService.instant('SETTINGS_VIEW.CHILDREN');
    return `${count} ${childText}`;
  }
}
